import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { processAssessment } from '../api';

const AssessmentV3 = () => {
  // Load saved state from localStorage
  const savedState = localStorage.getItem('assessmentFormData');
  const savedStep = localStorage.getItem('assessmentCurrentStep');
  
  const [currentStep, setCurrentStep] = useState(savedStep ? parseInt(savedStep) : 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [formData, setFormData] = useState(savedState ? JSON.parse(savedState) : {
    zipCode: '',
    propertyType: '',
    features: [],
    ownership: '',
    goals: [],
    email: ''
  });
  const navigate = useNavigate();

  const totalSteps = 6;
  const [showClearProgress, setShowClearProgress] = useState(!!savedState);
  
  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('assessmentFormData', JSON.stringify(formData));
  }, [formData]);
  
  useEffect(() => {
    localStorage.setItem('assessmentCurrentStep', currentStep.toString());
  }, [currentStep]);
  
  // Handle browser back button
  useEffect(() => {
    const handlePopstate = (e) => {
      e.preventDefault();
      if (currentStep > 1) {
        setCurrentStep(prev => prev - 1);
      } else {
        // Ask user if they want to leave the assessment
        if (window.confirm('Are you sure you want to leave the assessment? Your progress will be saved.')) {
          navigate(-1);
        }
      }
    };
    
    // Add a dummy history entry to intercept back button
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopstate);
    
    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, [currentStep, navigate]);
  
  const clearProgress = () => {
    if (window.confirm('Are you sure you want to clear your progress and start over?')) {
      localStorage.removeItem('assessmentFormData');
      localStorage.removeItem('assessmentCurrentStep');
      setFormData({
        zipCode: '',
        propertyType: '',
        features: [],
        ownership: '',
        goals: [],
        email: ''
      });
      setCurrentStep(1);
      setShowClearProgress(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked ? [...prev[name], value] : prev[name].filter(item => item !== value)
      }));
    } else if (type === 'radio') {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateCurrentStep = () => {
    switch(currentStep) {
      case 1:
        return formData.zipCode.trim() !== '';
      case 2:
        return formData.propertyType !== '';
      case 3:
        return formData.features.length > 0;
      case 4:
        return formData.ownership !== '';
      case 5:
        return formData.goals.length > 0;
      case 6:
        return formData.email.trim() !== '' && formData.email.includes('@');
      default:
        return true;
    }
  };
  
  const showValidationWarning = () => {
    const missingSteps = [];
    if (!formData.zipCode.trim()) missingSteps.push('ZIP code');
    if (!formData.propertyType) missingSteps.push('property type');
    if (formData.features.length === 0) missingSteps.push('property features');
    if (!formData.ownership) missingSteps.push('ownership status');
    if (formData.goals.length === 0) missingSteps.push('goals');
    
    alert(`Please complete the following: ${missingSteps.join(', ')}`);
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      if (currentStep === 6 && !validateAllSteps()) {
        showValidationWarning();
        return;
      }
      if (validateCurrentStep()) {
        setCurrentStep(prev => prev + 1);
      } else {
        alert('Please answer this question before continuing.');
      }
    } else {
      handleSubmit();
    }
  };
  
  const validateAllSteps = () => {
    return formData.zipCode.trim() !== '' &&
           formData.propertyType !== '' &&
           formData.features.length > 0 &&
           formData.ownership !== '' &&
           formData.goals.length > 0;
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.email.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }
    
    setIsSubmitting(true);
    setShowLoadingScreen(true);
    
    try {
      // Send only the actual data collected from Tier 1 form
      const transformedData = {
        email: formData.email,
        zipCode: formData.zipCode,
        propertyType: formData.propertyType,
        features: formData.features,
        goals: formData.goals,
        ownership: formData.ownership
      };
      
      const assessmentResult = await processAssessment(transformedData);
      
      // Clear localStorage on successful submission
      localStorage.removeItem('assessmentFormData');
      localStorage.removeItem('assessmentCurrentStep');
      
      // Add delay for loading animation
      await new Promise(resolve => setTimeout(resolve, 4000));
      
      // Navigate to results page with the public token
      navigate(`/assessment/${assessmentResult.publicToken}`);
    } catch (error) {
      console.error('Failed to process assessment:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to process assessment. Please try again.';
      alert(errorMessage);
      setShowLoadingScreen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const progressPercent = ((currentStep - 1) / (totalSteps - 1)) * 100;

  const getStepTitle = () => {
    switch(currentStep) {
      case 1: return "What is your ZIP code?";
      case 2: return "Which of these best describes your property?";
      case 3: return "What are the key characteristics of your property?";
      case 4: return "What is the ownership status of the property?";
      case 5: return "What are your main goals for building an ADU?";
      case 6: return "Where should we send your assessment?";
      default: return "ADU Feasibility Assessment";
    }
  };

  if (showLoadingScreen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <main className="container max-w-2xl">
          <div className="text-center mb-8">
            <div className="text-2xl font-semibold text-gray-500">ADU Lab</div>
          </div>
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-200">
            <div className="spinner w-12 h-12 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mx-auto mb-6"></div>
            <div className="text-xl font-medium mb-4">Generating your personalized analysis...</div>
            <p className="text-gray-600 text-sm">This can take up to 60 seconds as we run real calculations.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .assessment-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #f8f9fa;
          padding: 20px;
        }
        
        .assessment-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
          border: 1px solid #e0e0e0;
          width: 100%;
          max-width: 700px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        
        .card-header {
          padding: 24px 32px 20px;
          border-bottom: 1px solid #e0e0e0;
        }
        
        .form-content {
          flex: 1;
          padding: 32px;
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
        }
        
        /* Custom scrollbar for desktop */
        .form-content::-webkit-scrollbar {
          width: 6px;
        }
        
        .form-content::-webkit-scrollbar-track {
          background: #f1f3f4;
          border-radius: 3px;
        }
        
        .form-content::-webkit-scrollbar-thumb {
          background: #dadce0;
          border-radius: 3px;
        }
        
        .form-content::-webkit-scrollbar-thumb:hover {
          background: #bdc1c6;
        }
        
        .card-footer {
          padding: 20px 32px;
          border-top: 1px solid #e0e0e0;
          background: #fafafa;
        }
        
        .navigation-buttons {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .dash-avatar {
          flex-shrink: 0;
          width: 40px;
          height: 20px;
          position: relative;
          overflow: hidden;
          background: #1a73e8;
          border-radius: 10px;
        }
        
        .dash-avatar .pulse-bar {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 100%;
          height: 2px;
          color: white;
          background: linear-gradient(90deg, 
              transparent 0%, transparent 20%,
              currentColor 35%, currentColor 65%,
              transparent 80%, transparent 100%
          );
          animation: pulseMove 1.8s linear infinite;
        }
        
        @keyframes pulseMove {
          from { transform: translateX(-100%) translateY(-50%); }
          to { transform: translateX(100%) translateY(-50%); }
        }
        
        .choice-btn {
          display: block;
          padding: 16px 20px;
          min-height: 56px; /* Increased touch target for mobile */
          border: 1px solid #dadce0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-weight: 500;
          background: white;
          display: flex;
          align-items: center;
          -webkit-tap-highlight-color: transparent; /* Remove tap highlight on mobile */
        }
        
        .choice-btn:hover {
          background-color: #f1f3f4;
          border-color: #5f6368;
        }
        
        .choice-btn:has(input:checked) {
          background-color: #e8f0fe;
          border-color: #1a73e8;
          color: #1a73e8;
        }
        
        .form-step {
          animation: fadeIn 0.5s ease-in-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .spinner {
          animation: spin 1s linear infinite;
        }
        
        /* Mobile specific styles */
        @media (max-width: 640px) {
          .assessment-main {
            padding: 10px;
            padding-bottom: 90px;
          }
          
          .assessment-card {
            padding: 24px 20px;
          }
          
          .form-content {
            max-height: calc(100vh - 280px);
          }
          
          .form-navigation {
            padding: 12px 16px;
          }
          
          /* Ensure good touch targets on mobile */
          .choice-btn {
            min-height: 60px;
            margin-bottom: 12px;
          }
          
          /* Make input fields touch-friendly */
          input[type="text"], 
          input[type="email"] {
            min-height: 48px;
            font-size: 16px; /* Prevents zoom on iOS */
          }
        }
      `}</style>
      
      <div className="assessment-container">
        <div className="assessment-main">
          <div style={{ width: '100%', maxWidth: '700px' }}>
            <div className="assessment-card">
              <div className="text-center mb-6">
                <h1 className="text-3xl font-medium mb-2">{currentStep === 1 ? 'ADU Feasibility Assessment' : getStepTitle()}</h1>
                {currentStep === 1 && <p className="text-gray-600">Answer 6 quick questions to get started.</p>}
                {showClearProgress && currentStep > 1 && (
                  <div className="mt-3 text-sm">
                    <span className="text-green-600">✓ Progress saved</span>
                    <button 
                      onClick={clearProgress} 
                      className="ml-3 text-blue-600 hover:underline"
                    >
                      Start Over
                    </button>
                  </div>
                )}
              </div>
              
              <div className="w-full h-1 bg-gray-200 rounded-full mb-8 overflow-hidden">
                <div 
                  className="h-full bg-blue-600 transition-all duration-400 ease-out"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>

              <div className="form-content">
                <form onSubmit={(e) => e.preventDefault()} noValidate>
              {/* Step 1: ZIP Code */}
              {currentStep === 1 && (
                <div className="form-step">
                  <div className="mb-6">
                    <label className="text-lg font-medium mb-5 block">What is your ZIP code?</label>
                    <input 
                      type="text" 
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                      placeholder="e.g., 90210"
                      aria-label="ZIP code"
                      aria-required="true"
                    />
                    <div className="flex gap-3 items-center bg-gray-100 rounded-xl p-4 mt-6">
                      <div className="dash-avatar">
                        <div className="pulse-bar"></div>
                      </div>
                      <div className="text-sm text-gray-600 leading-relaxed">
                        We use the ZIP Code to analyze local zoning laws and market conditions for your potential ADU project.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Property Type */}
              {currentStep === 2 && (
                <div className="form-step">
                  <div className="mb-6">
                    <label className="text-lg font-medium mb-5 block">Which of these best describes your property?</label>
                    <div className="grid grid-cols-1 gap-3">
                      {['Single-Family', 'Two-Family', 'Three-Family', 'Multi-Family', 'Commercial', 'Other'].map((type) => (
                        <label key={type} className="choice-btn" role="radio" aria-checked={formData.propertyType === type.toLowerCase().replace('-', '-')}>
                          <input 
                            type="radio" 
                            name="propertyType" 
                            value={type.toLowerCase().replace('-', '-')}
                            onChange={handleInputChange}
                            className="hidden"
                            aria-label={`Property type: ${type}`}
                          />
                          <span>{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Property Features */}
              {currentStep === 3 && (
                <div className="form-step">
                  <div className="mb-6">
                    <label className="text-lg font-medium mb-5 block">What are the key characteristics of your property?</label>
                    <div className="grid grid-cols-1 gap-3">
                      {[
                        'Detached Garage',
                        'Basement Space',
                        'Side Yard or Backyard',
                        'Attached Garage',
                        'In-law Studio',
                        'Existing - Built'
                      ].map((feature) => (
                        <label key={feature} className="choice-btn" role="checkbox" aria-checked={formData.features.includes(feature.toLowerCase().replace(/ /g, '-'))}>
                          <input 
                            type="checkbox" 
                            name="features" 
                            value={feature.toLowerCase().replace(/ /g, '-')}
                            onChange={handleInputChange}
                            className="hidden"
                            aria-label={`Property feature: ${feature}`}
                          />
                          <span>{feature}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-3 items-center bg-gray-100 rounded-xl p-4 mt-6">
                      <div className="dash-avatar">
                        <div className="pulse-bar"></div>
                      </div>
                      <div className="text-sm text-gray-600 leading-relaxed">
                        This helps identify physical features that could support different, more cost-effective ADU types.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Ownership Status */}
              {currentStep === 4 && (
                <div className="form-step">
                  <div className="mb-6">
                    <label className="text-lg font-medium mb-5 block">What is the ownership status of the property?</label>
                    <div className="grid grid-cols-1 gap-3">
                      {[
                        'Own and Occupy',
                        'Own but Do Not Occupy',
                        'Considering Buying',
                        'Other'
                      ].map((status) => (
                        <label key={status} className="choice-btn" role="radio" aria-checked={formData.ownership === status.toLowerCase().replace(/ /g, '-')}>
                          <input 
                            type="radio" 
                            name="ownership" 
                            value={status.toLowerCase().replace(/ /g, '-')}
                            onChange={handleInputChange}
                            className="hidden"
                            aria-label={`Ownership status: ${status}`}
                          />
                          <span>{status}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-3 items-center bg-gray-100 rounded-xl p-4 mt-6">
                      <div className="dash-avatar">
                        <div className="pulse-bar"></div>
                      </div>
                      <div className="text-sm text-gray-600 leading-relaxed">
                        Some localities have 'Owner-Occupancy' requirements for ADU permits, so this is an important factor.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Goals */}
              {currentStep === 5 && (
                <div className="form-step">
                  <div className="mb-6">
                    <label className="text-lg font-medium mb-5 block">What are your main goals for building an ADU?</label>
                    <div className="grid grid-cols-1 gap-3">
                      {[
                        'Family/Caregiver',
                        'Aging in Place',
                        'Rental Income',
                        'Property Value',
                        'Legalize Unit',
                        'Other'
                      ].map((goal) => (
                        <label key={goal} className="choice-btn" role="checkbox" aria-checked={formData.goals.includes(goal.toLowerCase().replace(/[/\s]/g, '-'))}>
                          <input
                            type="checkbox"
                            name="goals"
                            value={goal.toLowerCase().replace(/[/\s]/g, '-')}
                            onChange={handleInputChange}
                            className="hidden"
                            aria-label={`ADU goal: ${goal}`}
                          />
                          <span>{goal}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 6: Email */}
              {currentStep === 6 && (
                <div className="form-step">
                  <div className="mb-6">
                    <label className="text-lg font-medium mb-5 block">Where should we send your ADU feasibility assessment?</label>
                    <input 
                      type="text" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                      placeholder="your@email.com"
                      aria-label="Email address"
                      aria-required="true"
                      aria-describedby="email-description"
                      autoComplete="email"
                    />
                    <div id="email-description" className="flex gap-3 items-center bg-gray-100 rounded-xl p-4 mt-6">
                      <div className="dash-avatar">
                        <div className="pulse-bar"></div>
                      </div>
                      <div className="text-sm text-gray-600 leading-relaxed">
                        We'll send your free analysis to your inbox, as well as display it on the next screen.
                      </div>
                    </div>
                  </div>
                </div>
              )}
              </form>
            </div>
          </div>
        </div>
      </div>
        
        {/* Fixed Navigation */}
        <div className="form-navigation">
          <div className="form-navigation-inner">
            <button 
              type="button" 
              onClick={prevStep}
              className={`px-8 py-3 rounded-full font-medium text-gray-600 transition-all hover:bg-gray-100 ${currentStep === 1 ? 'invisible' : ''}`}
            >
              Back
            </button>
            <button 
              type="button" 
              onClick={nextStep}
              disabled={isSubmitting}
              className="px-8 py-3 bg-blue-600 text-white rounded-full font-medium transition-all hover:shadow-md hover:bg-blue-700"
            >
              {currentStep === totalSteps ? 'Analyze My Property' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssessmentV3;